import moment from "moment-timezone";
import { Participant, AvailabilitySuggestedTime } from "../FindATime/types";

export type ParticipantSlotStatus = "available" | "busy" | "outside_hours" | "unknown";

export interface ParticipantStatus {
  participant: Participant;
  status: ParticipantSlotStatus;
  detail?: string;
}

export interface TimeSlotWithContext {
  time: Date;
  isSuggested: boolean;
  reason?: string;
  score?: number;
  participantStatuses: ParticipantStatus[];
}

function classifyParticipant(
  participant: Participant,
  slotStart: Date,
  durationMinutes: number,
): ParticipantStatus {
  if (participant.availabilitySource === "unknown") {
    return { participant, status: "unknown", detail: "Availability unknown" };
  }

  const startMs = slotStart.getTime();
  const endMs = startMs + durationMinutes * 60 * 1000;

  const conflict = participant.busyPeriods.find((bp) => {
    const bpStart = new Date(bp.startTime).getTime();
    const bpEnd = new Date(bp.endTime).getTime();
    return bpStart < endMs && bpEnd > startMs;
  });

  if (conflict) {
    return { participant, status: "busy", detail: conflict.label };
  }

  if (participant.workingHours && participant.timezone) {
    const m = moment.tz(slotStart, participant.timezone);
    const timeStr = m.format("HH:mm");
    if (timeStr < participant.workingHours.start || timeStr >= participant.workingHours.end) {
      return {
        participant,
        status: "outside_hours",
        detail: m.format("h:mm A z"),
      };
    }
  }

  return { participant, status: "available" };
}

export function generateTimeSlotsWithContext(
  participants: Participant[],
  suggestedTimes: AvailabilitySuggestedTime[],
  date: Date,
  timezone: string,
  durationMinutes: number,
): TimeSlotWithContext[] {
  const dayStart = moment.tz(date, timezone).startOf("day").add(7, "hours");
  const dayEnd = moment.tz(date, timezone).startOf("day").add(20, "hours");
  const slotInterval = 30;

  const slots: TimeSlotWithContext[] = [];
  const current = dayStart.clone();

  while (current.isBefore(dayEnd)) {
    const slotTime = current.toDate();
    const slotMs = slotTime.getTime();

    const matchedSuggestion = suggestedTimes.find((st) => {
      const stMs = new Date(st.startTime).getTime();
      return Math.abs(stMs - slotMs) < slotInterval * 60 * 1000;
    });

    const participantStatuses = participants.map((p) =>
      classifyParticipant(p, slotTime, durationMinutes),
    );

    slots.push({
      time: slotTime,
      isSuggested: !!matchedSuggestion,
      reason: matchedSuggestion?.reason,
      score: matchedSuggestion?.score,
      participantStatuses,
    });

    current.add(slotInterval, "minutes");
  }

  return slots;
}
